import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback
} from 'react';
import {
  db, collection, doc, addDoc, updateDoc, deleteDoc,
  query, onSnapshot, serverTimestamp
} from '../firebase/config';
import { useAuth } from './AuthContext';

const MaintenanceContext = createContext();

const computeNextDueDate = (lastServiceDate, intervalDays) => {
  if (!lastServiceDate || !intervalDays) return null;
  const d = new Date(lastServiceDate);
  d.setDate(d.getDate() + Number(intervalDays));
  return d.toISOString().split('T')[0];
};

const daysUntil = (nextDueDate) => {
  if (!nextDueDate) return null;
  const target = new Date(nextDueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

export const MaintenanceProvider = ({ children }) => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      console.log('[MaintContext] No user — clearing schedules');
      setSchedules([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const path = `users/${user.uid}/maintenance`;
    console.log('[MaintContext] Subscribing to:', path);

    const q = query(collection(db, 'users', user.uid, 'maintenance'));

    const unsub = onSnapshot(q, (snapshot) => {
      console.log('[MaintContext] Snapshot fired. Docs count:', snapshot.size);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('[MaintContext] Raw data:', data);
      const enriched = data.map(s => ({
        ...s,
        nextDueDate: s.nextDueDate || computeNextDueDate(s.lastServiceDate, s.intervalDays),
        daysUntilDue: daysUntil(s.nextDueDate || computeNextDueDate(s.lastServiceDate, s.intervalDays)),
      }));
      enriched.sort((a, b) => {
        const aD = a.daysUntilDue ?? 9999;
        const bD = b.daysUntilDue ?? 9999;
        return aD - bD;
      });
      console.log('[MaintContext] Enriched + sorted:', enriched.length, enriched);
      setSchedules(enriched);
      setLoading(false);
    }, (err) => {
      console.error('[MaintContext] Snapshot ERROR:', err);
      setLoading(false);
    });

    return () => {
      console.log('[MaintContext] Unsubscribing from:', path);
      unsub();
    };
  }, [user?.uid]);

  const addSchedule = useCallback(async (data) => {
    console.log('[MaintContext] addSchedule called with:', data);

    if (!user?.uid) {
      console.error('[MaintContext] addSchedule: no user');
      throw new Error('Must be signed in');
    }
    if (!data.category || !data.intervalDays || !data.lastServiceDate) {
      console.error('[MaintContext] addSchedule: missing required fields');
      throw new Error('category, intervalDays and lastServiceDate are required');
    }

    const nextDueDate = computeNextDueDate(data.lastServiceDate, data.intervalDays);

    const payload = {
      category: data.category,
      title: data.title || `${data.category} maintenance`,
      intervalDays: Number(data.intervalDays),
      lastServiceDate: data.lastServiceDate,
      nextDueDate,
      preferredProId: data.preferredProId || null,
      preferredProName: data.preferredProName || null,
      preferredServiceId: data.preferredServiceId || null,
      preferredServiceTitle: data.preferredServiceTitle || null,
      price: Number(data.price || 0),
      notes: data.notes || '',
      reminderEnabled: data.reminderEnabled !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    console.log('[MaintContext] Writing to Firestore:', payload);

    try {
      const ref = await addDoc(
        collection(db, 'users', user.uid, 'maintenance'),
        payload
      );
      console.log('[MaintContext] ✅ Write SUCCEEDED. New doc ID:', ref.id);
      return ref.id;
    } catch (err) {
      console.error('[MaintContext] ❌ Write FAILED:', err);
      throw err;
    }
  }, [user?.uid]);

  const updateSchedule = useCallback(async (id, updates) => {
    console.log('[MaintContext] updateSchedule called:', id, updates);
    if (!user?.uid || !id) return false;
    try {
      const final = { ...updates, updatedAt: serverTimestamp() };

      const intervalDays = final.intervalDays;
      const lastServiceDate = final.lastServiceDate;
      if (intervalDays || lastServiceDate) {
        const existing = schedules.find(s => s.id === id);
        const mergedInterval = intervalDays ?? existing?.intervalDays;
        const mergedLast = lastServiceDate ?? existing?.lastServiceDate;
        if (mergedInterval && mergedLast) {
          final.nextDueDate = computeNextDueDate(mergedLast, mergedInterval);
        }
      }

      await updateDoc(doc(db, 'users', user.uid, 'maintenance', id), final);
      console.log('[MaintContext] ✅ Update SUCCEEDED');
      return true;
    } catch (err) {
      console.error('[MaintContext] ❌ Update FAILED:', err);
      return false;
    }
  }, [user?.uid, schedules]);

  const deleteSchedule = useCallback(async (id) => {
    console.log('[MaintContext] deleteSchedule called:', id);
    if (!user?.uid || !id) return false;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'maintenance', id));
      console.log('[MaintContext] ✅ Delete SUCCEEDED');
      return true;
    } catch (err) {
      console.error('[MaintContext] ❌ Delete FAILED:', err);
      return false;
    }
  }, [user?.uid]);

  const markCompletedToday = useCallback(async (id) => {
    if (!user?.uid || !id) return false;
    const existing = schedules.find(s => s.id === id);
    if (!existing) return false;

    const today = new Date().toISOString().split('T')[0];
    const nextDueDate = computeNextDueDate(today, existing.intervalDays);

    try {
      await updateDoc(doc(db, 'users', user.uid, 'maintenance', id), {
        lastServiceDate: today,
        nextDueDate,
        updatedAt: serverTimestamp(),
      });
      console.log('[MaintContext] ✅ markCompletedToday SUCCEEDED');
      return true;
    } catch (err) {
      console.error('[MaintContext] ❌ markCompletedToday FAILED:', err);
      return false;
    }
  }, [user?.uid, schedules]);

  const overdue = useMemo(
    () => schedules.filter(s => s.daysUntilDue !== null && s.daysUntilDue < 0),
    [schedules]
  );
  const dueSoon = useMemo(
    () => schedules.filter(s => s.daysUntilDue !== null && s.daysUntilDue >= 0 && s.daysUntilDue <= 7),
    [schedules]
  );

  const value = useMemo(() => ({
    schedules,
    loading,
    overdue,
    dueSoon,
    addSchedule,
    updateSchedule,
    deleteSchedule,
    markCompletedToday,
  }), [
    schedules, loading, overdue, dueSoon,
    addSchedule, updateSchedule, deleteSchedule, markCompletedToday,
  ]);

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = () => {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return ctx;
};